import { formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Client } from '../../client/classes/client';
import {
    MeineKontakteDialogInput,
    MeineKontakteDialogResult,
} from '../../meine-kontakte/meine-kontakte-dialog/meine-kontakte-dialog';
import { MeineKontakteDialogComponent } from '../../meine-kontakte/meine-kontakte-dialog/meine-kontakte-dialog.component';
import { Tierliste } from '../../gruppe-tier/classes/tierliste';
import { BetriebskontaktAuswahlConfig } from '../../stammdaten/betriebskontakte/betriebskontakt-auswahl.component';
import { Betriebskontakt } from '../../stammdaten/betriebskontakte/betriebskontakt';
import { StammdatenService } from '../../stammdaten/stammdaten.service';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { BenutzerBetriebskontakteTierlisteService } from '../../../services/backend-api/benutzer-betriebskontakte-tierliste.service';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { MessageService } from '../../../services/message-service/message.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import {
    applyExternEmpfaengerPersistFields,
    applyInternZielbetriebPersistFields,
    applySchlachthofPersistField,
    filterAktiveBetriebskontakteTierlisteVerbindungen,
    mapBetriebskontakteTierlisteVerbindungenToKontakte,
    patchTierlisteFromExternEmpfaenger,
    patchTierlisteFromInternZielbetrieb,
    unwrapBetriebskontakteTierlisteReadResponse,
} from '../../gruppe-tier/tierliste-zielbetrieb-display.util';

export type AbgangZielFlowType = 'ausstallen_extern' | 'umstallen_intern';

export interface AusstallenEmpfaengerDialogData {
    tierliste: Tierliste;
    gruppeKundeId?: number;
    flowType?: AbgangZielFlowType;
    inhaberKundeId?: number | null;
    /** Kundenportal: Ausstalldatum im Empfänger-Dialog (nicht im Hauptfrontend). */
    showDatumAbgang?: boolean;
}

export interface AusstallenEmpfaengerDialogResult {
    tierliste: Tierliste;
    schlachthof: Client | null;
    kontakte: Betriebskontakt[];
    zielbetrieb?: Client | null;
    flowType?: AbgangZielFlowType;
}

const ZIELBETRIEB_LIST_LIMIT = 50;

@Component({
    selector: 'kiene-ausstallen-empfaenger-dialog',
    templateUrl: './ausstallen-empfaenger-dialog.component.html',
    styleUrls: ['./ausstallen-empfaenger-dialog.component.scss'],
})
export class AusstallenEmpfaengerDialogComponent implements OnInit, OnChanges, OnDestroy {
    @Input() embeddedInStepper = false;
    @Input() stepperTierliste: Tierliste | null = null;
    @Input() stepperGruppeKundeId?: number;
    @Input() stepperInhaberKundeId?: number | null;
    @Input() flowType: AbgangZielFlowType = 'ausstallen_extern';

    @Output() empfaengerSaved = new EventEmitter<AusstallenEmpfaengerDialogResult>();

    isLoading = true;
    isSaving = false;

    tierliste: Tierliste = new Tierliste();
    schlachthof: Client | null = null;
    selectedKontakte: Betriebskontakt[] = [];
    tempSelectedKontakte: Betriebskontakt[] = [];
    showKontakteAuswahl = true;

    kommentarCtrl = new FormControl('', { nonNullable: true });
    datumAbgangCtrl = new FormControl<Date | null>(null);
    zielbetriebSearchCtrl = new FormControl('', { nonNullable: true });
    zielbetriebKandidaten: Client[] = [];
    selectedZielbetrieb: Client | null = null;

    private loadedTierlisteId: number | null = null;
    private zielbetriebLoadSubscription?: Subscription;
    private zielbetriebSearchSubscription?: Subscription;

    kontakteConfig: BetriebskontaktAuswahlConfig = {
        showLandwirte: true,
        showHaendler: true,
        showSchlachthoefe: false,
        showAll: false,
        multiSelect: true,
        title: '',
        placeholder: 'Kontakte suchen...',
        compact: false,
        allowCreateNew: true,
        keepOpenOnSelect: true,
        alwaysExpanded: true,
        showApplyButton: false,
        modernStyle: true,
    };

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public readonly data: AusstallenEmpfaengerDialogData | null,
        @Optional() public readonly dialogRef: MatDialogRef<AusstallenEmpfaengerDialogComponent> | null,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly localStorageService: LocalStorageService,
        private readonly bbtService: BenutzerBetriebskontakteTierlisteService,
        private readonly stammdatenService: StammdatenService,
        private readonly dialog: MatDialog,
    ) {
        if (this.data?.tierliste) {
            this.tierliste = { ...this.data.tierliste };
        }
        if (this.data?.flowType) {
            this.flowType = this.data.flowType;
        }
    }

    ngOnInit(): void {
        const currentClient = this.localStorageService.getCurrentClient();
        if (currentClient?.betrieb_id) {
            this.kontakteConfig.betriebId = currentClient.betrieb_id;
        }
        this.kontakteConfig.adminMode = this.localStorageService.currentUserIsAdmin();
        this.kommentarCtrl.setValue(this.tierliste?.kommentar ?? '', { emitEvent: false });
        if (this.showDatumAbgang) {
            this.applyDefaultAusstalldatum();
        }

        if (this.embeddedInStepper) {
            this.setupEmbeddedFlowSubscriptions();
            this.syncEmbeddedFromInputs();
            return;
        }

        if (this.isUmstallenIntern) {
            this.setupEmbeddedFlowSubscriptions();
            this.prefillZielbetriebFromTierliste();
            this.loadZielbetriebListe();
            return;
        }
        this.prefillSchlachthofFromTierliste();
        this.loadGespeicherteKontakte();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['flowType']?.currentValue) {
            this.flowType = changes['flowType'].currentValue;
        }

        const isEmbedded =
            this.embeddedInStepper || changes['embeddedInStepper']?.currentValue === true;
        if (!isEmbedded) {
            return;
        }

        if (changes['flowType'] && !changes['flowType'].firstChange) {
            this.setupEmbeddedFlowSubscriptions();
        }

        if (changes['stepperTierliste'] || changes['flowType']) {
            this.syncEmbeddedFromInputs(!!changes['flowType'] && !changes['flowType'].firstChange);
        }
    }

    ngOnDestroy(): void {
        this.zielbetriebLoadSubscription?.unsubscribe();
        this.zielbetriebSearchSubscription?.unsubscribe();
    }

    get isUmstallenIntern(): boolean {
        return this.flowType === 'umstallen_intern';
    }

    get showDatumAbgang(): boolean {
        return this.data?.showDatumAbgang === true;
    }

    get shellEyebrow(): string {
        return this.isUmstallenIntern ? 'Umstallliste' : 'Ausstallliste';
    }

    get shellTitle(): string {
        return this.isUmstallenIntern ? 'Zielbetrieb auswählen' : 'Empfänger auswählen';
    }

    get shellIntro(): string {
        if (this.isUmstallenIntern) {
            return 'Wählen Sie optional den Betrieb, in den die Tiere umgestallt werden sollen (eigene und freigegebene Betriebe).';
        }
        return 'Wählen Sie optional einen Empfänger (Kontakt oder Schlachthof), bevor Sie Tiere zur Liste hinzufügen.';
    }

    onKontakteAusgewaehlt(kontakte: Betriebskontakt[]): void {
        this.selectedKontakte = [...kontakte];
        this.tempSelectedKontakte = [...kontakte];
    }

    onKontakteSelectionChanged(kontakte: Betriebskontakt[]): void {
        this.tempSelectedKontakte = [...kontakte];
    }

    get selectedKontaktCount(): number {
        return this.tempSelectedKontakte?.length || this.selectedKontakte?.length || 0;
    }

    get canSubmit(): boolean {
        if (this.isUmstallenIntern) {
            return !!this.selectedZielbetrieb?.kunde_id;
        }
        return !!this.schlachthof || this.selectedKontaktCount > 0;
    }

    get submitButtonLabel(): string {
        return this.canSubmit ? 'Übernehmen' : 'Überspringen';
    }

    get footerHint(): string {
        if (this.isUmstallenIntern) {
            if (!this.canSubmit) {
                return 'Zielbetrieb optional — ohne Auswahl fortfahren';
            }
            const name =
                this.selectedZielbetrieb?.bezeichnung_final ||
                this.selectedZielbetrieb?.bezeichnung ||
                'Zielbetrieb';
            return `${name} gewählt`;
        }
        if (!this.canSubmit) {
            return 'Empfänger optional — ohne Auswahl fortfahren';
        }
        const parts: string[] = [];
        if (this.schlachthof) {
            parts.push('1 Schlachthof');
        }
        if (this.selectedKontaktCount > 0) {
            parts.push(
                `${this.selectedKontaktCount} ${this.selectedKontaktCount === 1 ? 'Kontakt' : 'Kontakte'}`,
            );
        }
        return `${parts.join(' · ')} gewählt`;
    }

    isSameZielbetrieb(a: Client | null, b: Client | null): boolean {
        return KieneUtils.sameId(a?.kunde_id, b?.kunde_id);
    }

    waehleZielbetrieb(betrieb: Client): void {
        if (!betrieb?.kunde_id) {
            return;
        }
        this.selectedZielbetrieb = betrieb;
    }

    closeDialog(): void {
        if (!this.isSaving) {
            this.dialogRef?.close();
        }
    }

    openSchlachthofDialog(): void {
        const input = new MeineKontakteDialogInput(
            'Schlachthof',
            false,
            false,
            true,
            this.getGruppeKundeId() ?? 0,
        );
        this.dialog
            .open<MeineKontakteDialogComponent, MeineKontakteDialogInput, MeineKontakteDialogResult>(
                MeineKontakteDialogComponent,
                { maxWidth: '100vh', maxHeight: '90vh', width: '100%', data: input },
            )
            .afterClosed()
            .subscribe({
                next: result => {
                    if (result?.kunde) {
                        this.schlachthof = result.kunde;
                    }
                },
            });
    }

    deleteSchlachthof(): void {
        this.schlachthof = null;
    }

    save(): void {
        if (this.isSaving) {
            return;
        }
        if (this.isUmstallenIntern) {
            this.saveInternZielbetrieb();
            return;
        }
        this.saveExternEmpfaenger();
    }

    private saveInternZielbetrieb(): void {
        const sourceGruppeKundeId = this.getInhaberKundeId();
        this.applyFormFieldsToTierliste();

        if (!this.canSubmit) {
            this.persistKommentarThenFinish({
                tierliste: patchTierlisteFromInternZielbetrieb(this.tierliste, null, sourceGruppeKundeId),
                schlachthof: null,
                kontakte: [],
                zielbetrieb: null,
                flowType: 'umstallen_intern',
            });
            return;
        }

        const betrieb = this.selectedZielbetrieb;
        if (!betrieb?.kunde_id) {
            return;
        }

        if (!this.tierliste?.tierliste_id) {
            this.finishSave({
                tierliste: patchTierlisteFromInternZielbetrieb(this.tierliste, betrieb, sourceGruppeKundeId),
                schlachthof: null,
                kontakte: [],
                zielbetrieb: betrieb,
                flowType: 'umstallen_intern',
            });
            return;
        }

        this.isSaving = true;
        const payload: Tierliste = { ...this.tierliste };
        applyInternZielbetriebPersistFields(payload, betrieb, sourceGruppeKundeId);
        payload.haendler_uebernehmen = 0;
        if (!Number(payload.status_id)) {
            payload.status_id = 161;
        }

        this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', payload, undefined).subscribe({
            next: (liste: Tierliste) => {
                this.isSaving = false;
                const merged = patchTierlisteFromInternZielbetrieb(
                    { ...this.tierliste, ...(liste ?? payload) },
                    betrieb,
                    sourceGruppeKundeId,
                );
                this.finishSave({
                    tierliste: merged,
                    schlachthof: null,
                    kontakte: [],
                    zielbetrieb: betrieb,
                    flowType: 'umstallen_intern',
                });
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Zielbetrieb konnte nicht gespeichert werden.', err);
            },
        });
    }

    private saveExternEmpfaenger(): void {
        this.applyFormFieldsToTierliste();

        if (!this.canSubmit) {
            this.persistKommentarThenFinish({
                tierliste: patchTierlisteFromExternEmpfaenger(this.tierliste, [], null),
                schlachthof: null,
                kontakte: [],
                flowType: 'ausstallen_extern',
            });
            return;
        }
        const kontakte = this.tempSelectedKontakte?.length
            ? [...this.tempSelectedKontakte]
            : [...this.selectedKontakte];

        if (!this.tierliste?.tierliste_id) {
            this.finishSave({
                tierliste: patchTierlisteFromExternEmpfaenger(this.tierliste, kontakte, this.schlachthof),
                schlachthof: this.schlachthof,
                kontakte,
                flowType: 'ausstallen_extern',
            });
            return;
        }

        this.isSaving = true;
        applyExternEmpfaengerPersistFields(this.tierliste, kontakte);
        applySchlachthofPersistField(this.tierliste, this.schlachthof);

        this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', this.tierliste, undefined).subscribe({
            next: (liste: Tierliste) => {
                const kontaktIds = kontakte.filter(k => k.kontakt_id).map(k => k.kontakt_id!);
                const kontaktKundeIds: Record<number, number> = {};
                for (const kontakt of kontakte) {
                    const kontaktId = Number(kontakt?.kontakt_id);
                    const kundeId = Number(kontakt?.kunde_id);
                    if (kontaktId > 0 && kundeId > 0) {
                        kontaktKundeIds[kontaktId] = kundeId;
                    }
                }
                this.bbtService
                    .updateKontakteFuerTierliste(kontaktIds, liste.tierliste_id, {
                        allowClearAll: kontaktIds.length === 0,
                        kontaktKundeIds,
                    })
                    .subscribe({
                        next: () => {
                            const params = new HttpParams().set('tierliste_id', String(liste.tierliste_id));
                            this.api.get(this.apiBaseUrl + 'tierliste/read_one_kundenportal.php', params).subscribe({
                                next: (fresh: Tierliste) => {
                                    this.isSaving = false;
                                    const patched = patchTierlisteFromExternEmpfaenger(
                                        fresh,
                                        kontakte,
                                        this.schlachthof,
                                    );
                                    patched.kommentar = this.tierliste.kommentar;
                                    this.finishSave({
                                        tierliste: patched,
                                        schlachthof: this.schlachthof,
                                        kontakte,
                                        flowType: 'ausstallen_extern',
                                    });
                                },
                                error: err => {
                                    this.isSaving = false;
                                    this.messageService.errorMessage(
                                        'Tierliste konnte nach dem Speichern nicht geladen werden.',
                                        err,
                                    );
                                },
                            });
                        },
                        error: err => {
                            this.isSaving = false;
                            this.messageService.errorMessage('Kontakte konnten nicht gespeichert werden.', err);
                        },
                    });
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Tierliste konnte nicht gespeichert werden.', err);
            },
        });
    }

    private applyKommentarToTierliste(): void {
        this.tierliste.kommentar = (this.kommentarCtrl.value ?? '').trim();
    }

    private applyDatumAbgangToTierliste(): void {
        if (!this.showDatumAbgang) {
            return;
        }
        const formatted = this.formatDatumForApi(this.datumAbgangCtrl.value);
        if (formatted) {
            this.tierliste.datum_abgang = formatted as unknown as Date;
        }
    }

    private applyFormFieldsToTierliste(): void {
        this.applyKommentarToTierliste();
        this.applyDatumAbgangToTierliste();
    }

    private applyDefaultAusstalldatum(): void {
        const saved = this.parseAusstalldatum(this.tierliste?.datum_abgang);
        const value =
            saved ??
            (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return today;
            })();
        this.datumAbgangCtrl.setValue(value, { emitEvent: false });
    }

    private parseAusstalldatum(value: Date | string | null | undefined): Date | null {
        if (value == null || value === '') {
            return null;
        }
        const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        date.setHours(0, 0, 0, 0);
        return date;
    }

    private formatDatumForApi(value: Date | string | null | undefined): string | null {
        const parsed = this.parseAusstalldatum(value);
        if (!parsed) {
            return null;
        }
        return formatDate(parsed, 'yyyy-MM-dd', 'de', 'Europe/Berlin');
    }

    /** Beim Überspringen ohne Empfänger trotzdem Kommentar speichern. */
    private persistKommentarThenFinish(result: AusstallenEmpfaengerDialogResult): void {
        if (!this.tierliste?.tierliste_id) {
            this.finishSave(result);
            return;
        }

        this.isSaving = true;
        const payload: Tierliste = { ...result.tierliste };
        this.api.post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', payload, undefined).subscribe({
            next: (liste: Tierliste) => {
                this.isSaving = false;
                this.finishSave({
                    ...result,
                    tierliste: { ...result.tierliste, ...(liste ?? payload) },
                });
            },
            error: err => {
                this.isSaving = false;
                this.messageService.errorMessage('Kommentar konnte nicht gespeichert werden.', err);
            },
        });
    }

    private finishSave(result: AusstallenEmpfaengerDialogResult): void {
        if (this.embeddedInStepper) {
            this.empfaengerSaved.emit(result);
            return;
        }
        this.dialogRef?.close(result);
    }

    private getGruppeKundeId(): number | undefined {
        if (this.embeddedInStepper) {
            return this.stepperGruppeKundeId;
        }
        return this.data?.gruppeKundeId;
    }

    private getInhaberKundeId(): number | null {
        const fromInput = Number(this.stepperInhaberKundeId);
        if (fromInput > 0) {
            return fromInput;
        }
        const fromData = Number(this.data?.inhaberKundeId);
        if (fromData > 0) {
            return fromData;
        }
        return Number(this.tierliste?.kunde_id_abgang) || null;
    }

    private setupEmbeddedFlowSubscriptions(): void {
        if (!this.isUmstallenIntern || this.zielbetriebSearchSubscription) {
            return;
        }
        this.zielbetriebSearchSubscription = this.zielbetriebSearchCtrl.valueChanges
            .pipe(debounceTime(300))
            .subscribe(() => this.loadZielbetriebListe());
    }

    private syncEmbeddedFromInputs(forceReload = false): void {
        const liste = this.stepperTierliste;
        if (liste?.tierliste_id) {
            this.applyTierliste(liste, forceReload);
            return;
        }
        this.isLoading = false;
    }

    private applyTierliste(liste: Tierliste, forceReload = false): void {
        const nextId = liste?.tierliste_id ?? null;
        const sameList = !forceReload && nextId !== null && nextId === this.loadedTierlisteId;
        this.tierliste = { ...liste };
        this.kommentarCtrl.setValue(liste?.kommentar ?? '', { emitEvent: false });
        if (this.showDatumAbgang) {
            this.applyDefaultAusstalldatum();
        }

        if (sameList) {
            if (this.isUmstallenIntern) {
                return;
            }
            if (!this.isLoading && this.selectedKontakte.length === 0 && this.tempSelectedKontakte.length === 0) {
                this.prefillSchlachthofFromTierliste();
                this.loadGespeicherteKontakte();
            }
            return;
        }

        this.loadedTierlisteId = nextId;
        if (this.isUmstallenIntern) {
            this.selectedZielbetrieb = null;
            this.zielbetriebKandidaten = [];
            this.prefillZielbetriebFromTierliste();
            this.loadZielbetriebListe();
            return;
        }
        this.selectedKontakte = [];
        this.tempSelectedKontakte = [];
        this.prefillSchlachthofFromTierliste();
        this.loadGespeicherteKontakte();
    }

    private prefillZielbetriebFromTierliste(): void {
        const kundeId = Number(this.tierliste?.kunde_id_einstallen);
        if (kundeId <= 0) {
            this.selectedZielbetrieb = null;
            return;
        }
        const betrieb = new Client();
        betrieb.kunde_id = kundeId;
        betrieb.bezeichnung = this.tierliste.kunde_einstallen;
        betrieb.bezeichnung_final = this.tierliste.kunde_einstallen;
        betrieb.zusatz = this.tierliste.kunde_zusatz_einstallen;
        betrieb.zusatz_final = this.tierliste.kunde_zusatz_einstallen;
        betrieb.vvvo = this.tierliste.kunde_vvvo_einstallen;
        betrieb.strasse = this.tierliste.kunde_strasse_einstallen;
        betrieb.plz = this.tierliste.kunde_plz_einstallen;
        betrieb.ort = this.tierliste.kunde_ort_einstallen;
        this.selectedZielbetrieb = betrieb;
    }

    private loadZielbetriebListe(): void {
        this.isLoading = true;
        this.zielbetriebLoadSubscription?.unsubscribe();
        const search = this.zielbetriebSearchCtrl.value ?? '';
        this.zielbetriebLoadSubscription = this.stammdatenService
            .loadZielbetriebeForUmstallen(this.getInhaberKundeId(), search, ZIELBETRIEB_LIST_LIMIT)
            .subscribe({
                next: resp => {
                    this.zielbetriebKandidaten = resp?.records ?? [];
                    if (!search && this.zielbetriebKandidaten.length === 1 && !this.selectedZielbetrieb) {
                        this.waehleZielbetrieb(this.zielbetriebKandidaten[0]);
                    }
                    this.isLoading = false;
                },
                error: () => {
                    this.zielbetriebKandidaten = [];
                    this.isLoading = false;
                },
            });
    }

    private prefillSchlachthofFromTierliste(): void {
        if (!this.tierliste?.kunde_id_schlachthof) {
            this.schlachthof = null;
            return;
        }
        this.schlachthof = new Client();
        this.schlachthof.kunde_id = this.tierliste.kunde_id_schlachthof;
        this.schlachthof.bezeichnung = this.tierliste.kunde_schlachthof;
        this.schlachthof.zusatz = this.tierliste.kunde_zusatz_schlachthof;
        this.schlachthof.vvvo = this.tierliste.kunde_vvvo_schlachthof;
        this.schlachthof.strasse = this.tierliste.kunde_strasse_schlachthof;
        this.schlachthof.plz = this.tierliste.kunde_plz_schlachthof;
        this.schlachthof.ort = this.tierliste.kunde_ort_schlachthof;
    }

    private loadGespeicherteKontakte(): void {
        const tierlisteId = this.tierliste?.tierliste_id;
        if (!tierlisteId) {
            this.selectedKontakte = [];
            this.tempSelectedKontakte = [];
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.bbtService.getVerbindungenFuerTierliste(tierlisteId).subscribe({
            next: result => {
                const aktive = filterAktiveBetriebskontakteTierlisteVerbindungen(
                    unwrapBetriebskontakteTierlisteReadResponse(result),
                );
                this.selectedKontakte = mapBetriebskontakteTierlisteVerbindungenToKontakte(aktive);
                this.tempSelectedKontakte = [...this.selectedKontakte];
                this.kontakteConfig = {
                    ...this.kontakteConfig,
                    zusaetzlicheKontakte: [...this.selectedKontakte],
                };
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }
}
